/**
 * Faceta Mensagens do Localizador — a seção LENTA, e a única que dispara depois
 * das outras.
 *
 * Três coisas a manter em mente ao mexer aqui:
 *
 * 1. **3 caracteres é limite do ÍNDICE**, não de gosto. Abaixo disso o trigram
 *    não cobre e a consulta volta a seq scan (medido: 383 ms em 71.936
 *    mensagens contra 0,7 ms com o índice).
 * 2. **`loading` é parte do contrato**, não detalhe: a seção nasce com a altura
 *    que vai ter, para que quando os dados chegarem nada abaixo se mova. Seção
 *    que aparece do nada e empurra o resto faz o operador clicar no item errado.
 * 3. **`attribution` é obrigatório na prática.** Busca por substring produz
 *    ruído legítimo — o termo «mari» casa «marido». Sem quem/quando abaixo do
 *    trecho, o operador não descarta o falso positivo sem abrir a conversa.
 *
 * Regra do silêncio: o engine devolve `meta.enabled = false` quando falta flag
 * ou capability. Aí a seção não existe — nem como cabeçalho.
 */
import { useQuery } from '@tanstack/react-query';

import { useCapability } from '../../hooks/use-capability';
import { api } from '../../lib/api-client';

import { useDebounced } from './use-debounced';

import type { SpotlightItem } from './types';

export const MESSAGE_MIN_CHARS = 3;
export const MESSAGE_LIMIT = 5;

export function useMessageHits(term: string): { items: SpotlightItem[]; loading: boolean } {
  // A capability é checada no servidor (regra do silêncio); aqui ela evita o
  // request inútil — «gatear o fetch, não só o render».
  const canSearch = useCapability('message-content-search');
  const debounced = useDebounced(term.trim());
  const enabled = canSearch && debounced.length >= MESSAGE_MIN_CHARS;

  const query = useQuery({
    queryKey: ['messages-search', debounced, MESSAGE_LIMIT],
    queryFn: () => api.searchMessageContent({ q: debounced, limit: MESSAGE_LIMIT }),
    enabled,
    staleTime: 30 * 1000,
  });

  if (!enabled) return { items: [], loading: false };

  // O servidor disse que não está ligado: a seção deixa de existir, sem
  // cabeçalho e sem esqueleto.
  if (query.data !== undefined && !query.data.meta.enabled) {
    return { items: [], loading: false };
  }

  const items: SpotlightItem[] = (query.data?.data ?? []).map((hit) => ({
    key: `message:${hit.messageId}`,
    section: 'messages' as const,
    label: hit.excerpt,
    excerpt: hit.excerpt,
    // «quem · quando». O QUEM vem primeiro porque é ele que permite descartar o
    // falso positivo de substring; a data só ordena a leitura.
    attribution: [hit.personDisplayName, formatAttribution(hit.sentAt)]
      .filter((part): part is string => part !== null && part !== '')
      .join(' · '),
    to: `/atendimento/conversation/${hit.conversationId}`,
  }));

  return { items, loading: query.isLoading };
}

/** «há 2 d» / «28/07» — curto o bastante para caber na linha. */
function formatAttribution(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const diffH = Math.floor(diffMs / 3_600_000);
  if (diffH < 1) return 'há minutos';
  if (diffH < 24) return `há ${String(diffH)} h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `há ${String(diffD)} d`;
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' }).format(d);
}
